import type React from 'react';
import { useMemo } from 'react';
import styles from '@/components/Title/Title.module.css';
import { cn } from '@/utils';

type TitleLevel = 1 | 2 | 3 | 4 | 5 | 6;
type TitleSize = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface TitleProps {
	className?: string;
	level: TitleLevel;
	size: TitleSize;
	children: React.ReactNode;
	fontSize?: number | string;
	lineHeight?: number | string;
}

const Title: React.FC<TitleProps> = ({
	className,
	level,
	size,
	children,
	fontSize,
	lineHeight
}) => {
	const HeadingTag: React.ElementType = `h${level}`;

	const computedFontSize = useMemo(() => {
		if (!fontSize) {
			return undefined;
		}
		return typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
	}, [fontSize]);

	const computedLineHeight = useMemo(() => {
		if (!lineHeight) {
			return undefined;
		}
		return typeof lineHeight === 'number' ? `${lineHeight}px` : lineHeight;
	}, [lineHeight]);

	return (
		<HeadingTag
			className={cn(
				{
					[styles.h1]: size === 'h1',
					[styles.h2]: size === 'h2',
					[styles.h3]: size === 'h3',
					[styles.h4]: size === 'h4',
					[styles.h5]: size === 'h5',
					[styles.h6]: size === 'h6'
				},
				className
			)}
			style={{
				fontSize: computedFontSize,
				lineHeight: computedLineHeight
			}}
		>
			{children}
		</HeadingTag>
	);
};

export default Title;
